import type { ReactNode } from 'react';
import { cn } from '@shared/lib/cn';

export interface Column<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  /** Numeric cells align to the end side with tabular digits (design system §4.14). */
  numeric?: boolean;
  /** Hide on the phone card layout (e.g. redundant ids). */
  hideOnCard?: boolean;
  className?: string;
}

export interface TableProps<Row> {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  /** Visually hidden caption describing the table for screen readers. */
  caption: string;
  empty?: ReactNode;
  loading?: boolean;
  loadingRows?: number;
  className?: string;
}

/**
 * Semantic table. DOM order is the logical order; `dir="rtl"` on <html> puts the first column at
 * the right — the column array is never reversed. Below md, rows collapse to label/value cards.
 */
export function Table<Row>({ columns, rows, rowKey, caption, empty, loading, loadingRows = 3, className }: TableProps<Row>) {
  if (!loading && rows.length === 0 && empty) return <>{empty}</>;
  return (
    <div className={cn('rounded-lg border border-border-subtle bg-surface', className)}>
      <table className="hidden w-full border-collapse text-sm md:table" aria-busy={loading || undefined}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-border-subtle">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cn('px-4 py-3 font-medium text-secondary', c.numeric ? 'text-end' : 'text-start', c.className)}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: loadingRows }, (_, i) => (
                <tr key={`sk${i}`} className="border-b border-border-subtle last:border-b-0">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3">
                      <div className="skeleton h-4 w-3/4" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => (
                <tr key={rowKey(row)} className="border-b border-border-subtle last:border-b-0 hover:bg-surface-tint">
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={cn('px-4 py-3 align-middle text-primary', c.numeric ? 'tabular text-end' : 'text-start', c.className)}
                    >
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>

      {/* Phone: card list */}
      <ul className="divide-y divide-border-subtle md:hidden" aria-label={caption} aria-busy={loading || undefined}>
        {loading
          ? Array.from({ length: loadingRows }, (_, i) => (
              <li key={`sk${i}`} className="flex flex-col gap-2 p-4">
                <div className="skeleton h-4 w-1/2" />
                <div className="skeleton h-3 w-3/4" />
              </li>
            ))
          : rows.map((row) => (
              <li key={rowKey(row)} className="p-4">
                <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-4 gap-y-2 text-sm">
                  {columns
                    .filter((c) => !c.hideOnCard)
                    .map((c) => (
                      <div key={c.key} className="contents">
                        <dt className="text-muted">{c.header}</dt>
                        <dd className={cn('min-w-0 text-primary', c.numeric && 'tabular')}>{c.cell(row)}</dd>
                      </div>
                    ))}
                </dl>
              </li>
            ))}
      </ul>
    </div>
  );
}
